import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, Clock, CheckCircle, XCircle, Send,
  Copy, ExternalLink, AlertCircle, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPanel({ booking, onUpdate }) {
  const { getAuthHeaders } = useAuth();
  const [payment, setPayment] = useState(null);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!booking?.id) return;
    axios.get(`${API}/payments/booking/${booking.id}`, { headers: getAuthHeaders() })
      .then(res => { if (res.data?.id) setPayment(res.data); })
      .catch(() => {});
  }, [booking?.id]);

  if (!booking) return null;
  if (booking.is_refund) return null;

  const paymentStatus = booking.payment_status || "unpaid";
  const amount = booking.selling_price || 0;
  const passengers = booking.passengers || [];
  const customerEmail = passengers[0]?.email || "";
  const checkoutUrl = payment?.checkout_url || booking?.payment_url || "";

  const statusBadge = () => {
    if (paymentStatus === "paid") return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
    if (paymentStatus === "awaiting_payment") return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Awaiting Payment</Badge>;
    if (paymentStatus === "pay_later") return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pay Later</Badge>;
    if (paymentStatus === "expired") return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
    return <Badge className="bg-gray-100 text-gray-600"><AlertCircle className="w-3 h-3 mr-1" />Not Paid</Badge>;
  };

  const sendTikkieLink = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/payments/create`,
        { booking_id: booking.id },
        { headers: getAuthHeaders() }
      );
      setPayment(res.data);
      toast.success(res.data.email_sent ? "Tikkie payment link sent to customer" : "Tikkie payment link created");
      setMode(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment link");
    } finally { setSending(false); }
  };

  const sendPayLater = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/payments/pay-later`,
        { booking_id: booking.id, due_date: dueDate, notes },
        { headers: getAuthHeaders() }
      );
      toast.success("Pay later confirmed — email sent to customer");
      setMode(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied!");
  };

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-blue-800 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Payment
          </span>
          {statusBadge()}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">

        {/* Amount */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
          <span className="text-sm text-gray-500">Amount Due</span>
          <span className="font-bold text-lg">€{Number(amount).toFixed(2)}</span>
        </div>

        {/* No email warning */}
        {!customerEmail && (
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-xs text-amber-700 border border-amber-200">
            <AlertCircle className="w-3 h-3 shrink-0" />
            No customer email on file — link will be created but not auto-sent
          </div>
        )}

        {/* Existing payment link */}
        {checkoutUrl && paymentStatus !== "paid" && (
          <div className="space-y-1.5">
            <p className="text-xs text-gray-500">Payment link</p>
            <div className="flex gap-1.5">
              <input readOnly value={checkoutUrl}
                className="flex-1 text-xs border rounded px-2 py-1.5 bg-white text-gray-500 truncate min-w-0" />
              <button onClick={copyLink} title="Copy"
                className="p-1.5 border rounded bg-white hover:bg-gray-50 text-gray-500 shrink-0">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" title="Open"
                className="p-1.5 border rounded bg-white hover:bg-gray-50 text-blue-500 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}

        {/* Pay later info */}
        {paymentStatus === "pay_later" && booking.pay_later_due_date && (
          <div className="p-2 bg-amber-50 rounded text-xs text-amber-700 border border-amber-200">
            Due: <strong>{booking.pay_later_due_date}</strong>
            {booking.pay_later_notes && <p className="mt-0.5">{booking.pay_later_notes}</p>}
          </div>
        )}

        {/* Paid */}
        {paymentStatus === "paid" && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">Payment received</p>
              {booking.paid_at && (
                <p className="text-xs text-green-600">
                  {new Date(booking.paid_at).toLocaleDateString("nl-NL")}
                  {booking.payment_method && ` · ${booking.payment_method.toUpperCase()}`}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {paymentStatus !== "paid" && mode === null && (
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" onClick={() => setMode("tikkie")}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
              <Send className="w-3 h-3 mr-1" />
              {checkoutUrl ? "Resend Link" : "Send Payment Link"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode("pay_later")}
              className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs">
              <Clock className="w-3 h-3 mr-1" /> Pay Later
            </Button>
          </div>
        )}

        {/* Tikkie form */}
        {mode === "tikkie" && (
          <div className="p-3 bg-white rounded-lg border border-blue-200 space-y-3">
            <p className="text-xs font-medium text-blue-800">
              Send Tikkie payment request
              {customerEmail && <span className="text-gray-400"> → sent to {customerEmail}</span>}
            </p>
            <p className="text-xs text-gray-500">
              Customer pays easily via Tikkie — opens directly in their banking app. Link valid 14 days.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={sendTikkieLink} disabled={sending}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs">
                {sending
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Creating...</>
                  : <><Send className="w-3 h-3 mr-1" />Create & Send</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode(null)} className="text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Pay Later form */}
        {mode === "pay_later" && (
          <div className="p-3 bg-white rounded-lg border border-amber-200 space-y-3">
            <p className="text-xs font-medium text-amber-800">Pay Later — Trusted Customer</p>
            <div>
              <Label className="text-xs">Due Date (optional)</Label>
              <Input type="date" value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Pay within 3 days by bank transfer"
                className="mt-1 text-sm" />
            </div>
            <p className="text-xs text-gray-400">
              Customer gets a confirmation email with your bank transfer details.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={sendPayLater} disabled={sending}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs">
                {sending ? "Sending..." : "Confirm Pay Later"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode(null)} className="text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
